import { Observable } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { isEdata } from '@root/constant';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { HTTP_HEADERS, HTTP_METHODS } from '@root/constant';
import { EncryptionProviderService } from '@shared/encryption.service';
import { AuthService } from '@shared/auth.service';

export abstract class AbstractService<T> {
  public http: HttpClient;

  public url: string;

  public encryptionProviderService: EncryptionProviderService;

  constructor(
    http: HttpClient,
    url: string,
    encryptionProviderService: EncryptionProviderService
  ) {
    this.http = http;
    this.url = url;
  }

  public request(options: {
    method: string;
    url: string;
    body?: any;
    headers?: {
      [header: string]: string | string[];
    };
    params?: {
      [param: string]: string | string[];
    };
    /**
     * If not provided will be derived from Accept header
     */
    responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
  }): Observable<any> {
    options.headers = options.headers ? { ...options.headers } : {};
    // set Content-Type only if not set and not multipart
    const contentType = options.headers[HTTP_HEADERS.CONTENT_TYPE];

    if (!contentType) {
      options.headers[HTTP_HEADERS.CONTENT_TYPE] =
        'application/json;charset=utf-8';
    } else if (contentType === 'multipart/form-data') {
      // lets the browser detect the correct Content-Type as multipart/form-data, and fill in the correct boundary.
      delete options.headers[HTTP_HEADERS.CONTENT_TYPE];
    }

    if (isEdata && options.method === HTTP_METHODS.POST) {
      options.body = !options.body ? {} : options.body;
      options.body.guid = this.generateGuid(options.url);
      options.body.timeStamp = Date.now();

      options.body = {
        eData: this.encryptionProviderService.encryptData(
          JSON.stringify(options.body)
        ),
      };
    }

    // set Accept only if not set
    if (!options.headers[HTTP_HEADERS.ACCEPT]) {
      options.headers[HTTP_HEADERS.ACCEPT] = 'application/json;charset=utf-8';
    }

    // direct execute check - download
    if (
      options.headers[HTTP_HEADERS.BLOB] &&
      options.method === HTTP_METHODS.GET
    ) {
      delete options.headers[HTTP_HEADERS.BLOB];
      options.responseType = 'blob';
    }

    // set session token if logged in user
    if (AuthService.isLoggedIn()) {
      options.headers['session-token'] = AuthService.getToken();
    }

    if (options.body instanceof FormData && options.method === 'POST') {
      delete options.headers[HTTP_HEADERS.CONTENT_TYPE];
    }

    return Observable.create((observer) => {
      this.http
        .request(options.method, options.url, {
          headers: options.headers,
          body: options.body,
          params: options.params,
          observe: 'response',
          responseType: options.responseType,
        })
        .pipe(timeout(60000))
        .subscribe(
          (data) => {
            // since observe is set to response
            const resp = data as HttpResponse<any>;
            const o = resp.body;
            if (o['eData']) {
              const decryptRes = this.encryptionProviderService.decryptData(
                o['eData']
              );
              observer.next(JSON.parse(decryptRes));
            } else {
              observer.next(o);
            }
          },
          (error) => {
            observer.error(error.error);
          },
          () => observer.complete()
        );
    });
  }

  generateGuid(url: string) {
    let result = '';
    const length = 32;
    const chars =
      '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    for (let i = length; i > 0; --i) {
      result += chars[Math.round(Math.random() * (chars.length - 1))];
    }

    this.encryptionProviderService.setGuid({
      key: url,
      value: result,
    });

    return result;
  }
}
